import { Component, inject, Input } from '@angular/core';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { DashboardService } from '../../../services/dashboard.service';
import { SummaryRequest } from '../../../../../_shared/models';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ModalService } from '../../../../../_shared/services/modals/modal.service';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { KpiWidgetComponent } from "../../../../../_shared/components/kpi-widget/kpi-widget.component";
import { AvailableAssetsBreakdownModalComponent } from '../../modals/available-assets-breakdown-modal/available-assets-breakdown-modal.component';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { formatCost, formatCostDifference } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-available-assets-widget',
  standalone: true,
  imports: [KpiWidgetComponent],
  templateUrl: './available-assets-widget.component.html',
  styleUrl: './available-assets-widget.component.scss'
})
export class AvailableAssetsWidgetComponent extends BaseWidgetComponent {
  private readonly transloco = inject(TranslocoService);
  @Input() selectedFilter: string;

  hasError: boolean = false;
  errorMessage: string = '';
  currencySymbol: string = '';
  currencyCode: string = '';

  private dashboardService = inject(DashboardService);
  private errorService = inject(ErrorHandlerService);
  private modalService = inject(ModalService);
  private authService = inject(AuthService);

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.title = inject(TranslocoService).translate('maintenanceDashboard.widgets.availableAssets');
    this.isLoading = true;
    this.value = 0;
    this.percentageDifference = 0;
    this.totalCost = 0;
    this.difference = 0;

    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    try {
      this.cancelSubject.next();

      const request: SummaryRequest = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const response = await this.dashboardService.getAvailableAssetsStats(
        request,
        this.cancelSubject
      );

      this.value = response?.data?.totalValue ?? 0;
      this.percentageDifference = response?.data?.percentageChange ?? 0;
      this.totalCost = response?.data?.cost ?? 0;
      this.difference = response?.data?.difference ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: (errorMessage: string) => {
          this.errorMessage = errorMessage;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  public override formatValue(value: number): string {
    return value.toLocaleString();
  }

  formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  openBreakdownModal(): void {
    this.modalService.open(AvailableAssetsBreakdownModalComponent, {
      size: 'lg',
      centered: true
    });
  }
}